import React, { Component, Fragment } from "react";
import { Container, Card, CardBody } from "reactstrap";

import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import history from "../../history";
import PageTitle from "../../Layout/AppMain/PageTitle";
import { Link } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import moment from "moment";
import { Toast } from "primereact/toast";
import { utils } from "../../utils/utils";
import _ from "lodash";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Column } from "primereact/column";
import { stringFilterMatchModeOptions, numberFilterMatchModeOptions, dateFilterTemplate } from "../Paginator";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import { IssuingClient, IssuingClientStatus } from "../../models/IssuingClientsModel";
import { userApiService } from "../../services/api/users/UsersApiService";
import { Dialog } from "primereact/dialog";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import { User } from "../../models/User";
import { AppCard, DataTableToolbar, PageLayout } from "../../components/ui";
import { Plus, Users } from "lucide-react";

interface IssueingClientState {
    clients: IssuingClient[];
    globalFilterValue: string;
    isLoading: boolean;
    totalRecords: number;
    showAddDialog: boolean;
    selectedUser: User | null;
    individuals: User[];
    usersMap: Record<string, User>;
}

export default class IssuingClients extends Component<Record<string, never>, IssueingClientState> {
    private toast: Toast | null = null;
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            clients: [],
            globalFilterValue: "",
            isLoading: false,
            totalRecords: 0,
            showAddDialog: false,
            selectedUser: null,
            individuals: [],
            usersMap: {}
        };
    }

    loadClientsData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        try {
            // ---------------------------
            // 1. Load clients
            // ---------------------------
            searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

            const iCardClients = await issuingApiService.getClients({ ...searchRequest });

            if (!iCardClients.success) {
                this.toast?.show({
                    severity: "error",
                    summary: "Error",
                    detail: iCardClients.message,
                    life: 3000
                });

                this.setState({ isLoading: false });
                return;
            }

            const clients: IssuingClient[] = iCardClients.data || [];

            // ---------------------------
            // 2. Extract unique IDs
            // ---------------------------
            const individualIds = _.uniq(
                clients.map(c => c.individualId).filter(Boolean)
            );

            // ---------------------------
            // 3. Fetch users by ID (NO request object, direct call)
            // ---------------------------
            let users: User[] = [];

            if (individualIds.length > 0) {
                const results = await Promise.all(
                    individualIds.map(id => userApiService.get(id))
                );

                users = results
                    .filter(r => r?.success && r.data)
                    .map(r => r.data);
            }

            // ---------------------------
            // 4. Map users
            // ---------------------------
            const usersMap = _.keyBy(users, "id");

            // ---------------------------
            // 5. Enrich clients
            // ---------------------------
            const enrichedClients = clients.map(client => {
                const user = usersMap[client.individualId];

                return {
                    ...client,
                    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
                };
            });

            // ---------------------------
            // 6. Save
            // ---------------------------
            this.setState({
                clients: enrichedClients,
                totalRecords: iCardClients.totalCount,
                isLoading: false
            });

        } catch (error: any) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: error?.message || "Unexpected error",
                life: 3000
            });

            this.setState({ isLoading: false });
        }
    };

    getClientDetailsPath = (client: IssuingClient) => `/issuing/individualId/${client.individualId}`;

    // Wraps a cell in a real link: a plain click navigates in the same tab, while right-click
    // ("Open link in new tab"), middle-click and Ctrl/Cmd+click get the browser's new-tab behaviour.
    // stopPropagation keeps onRowClick from navigating a second time.
    renderRowLink = (client: IssuingClient, content: React.ReactNode) => (
        <Link
            to={this.getClientDetailsPath(client)}
            style={{ color: "inherit", textDecoration: "none", display: "block" }}
            onClick={(e) => e.stopPropagation()}
        >
            {content}
        </Link>
    );

    onClientDetailsActionButtonClick = (event: any) => {
        const client = event.data as IssuingClient;

        history.push(this.getClientDetailsPath(client));
    };


    openAddClientDialog = async () => {
        this.setState({ showAddDialog: true });
        var searchRequest = new SearchRequestBaseModel();
        await this.loadUsersData(searchRequest);

    };

    actionBodyTemplateUser = (rowData: User) => {
        return <Button
            label="Enroll"
            icon="pi pi-check"
            onClick={() => this.handleEnrollClient(rowData)}
        />
    };

    renderStatusBadge = (status?: IssuingClientStatus) => {
        if (!status) {
            return <span className="badge badge-secondary">-</span>;
        }

        return (
            <span
                className={classNames(
                    "badge badge-pill px-3 py-2",
                    utils.getClientStatusBadge(status)
                )}
            >
                {IssuingClientStatus[
                    status as keyof typeof IssuingClientStatus
                ]}
            </span>
        );
    };
    handleEnrollClient = async (rowData: User) => {

        this.setState({ isLoading: true });

        const response = await issuingApiService.updateClientDetails({
            individualId: rowData.id
        });

        if (!response.success) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: response.message,
                life: 3000
            });
        } else {
            this.toast?.show({
                severity: "success",
                summary: "Success",
                detail: "Client enrolled successfully",
                life: 3000
            });

            this.setState({ showAddDialog: false, selectedUser: null });

            // reload table
            this.dataTable?.reset();
            // this.loadClientsData();
        }

        this.setState({ isLoading: false });
    };

    renderHeader = (hasActiveFilters: boolean) => {
        return (
            <DataTableToolbar
                onClear={() => this.dataTable!.reset()}
                hasActiveFilters={hasActiveFilters}
            />
        );
    };
    renderPageActions = () => {
        return (
            <>

                <Button
                    label="Add Client"
                    icon={<Plus size={16} />}
                    outlined
                    onClick={() => {
                        this.openAddClientDialog();
                    }}
                />

            </>
        );
    };
    onUserSelect = (user: User) => {
        this.setState({ selectedUser: user });
    };
    loadUsersData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        const response = await userApiService.onlyIndividuals(searchRequest);

        if (!response.success) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: response.message,
                life: 3000
            });
        }
        const usersMap = _.keyBy(response.data, "id");

        this.setState({
            individuals: response.data,
            usersMap,
            totalRecords: response.totalCount,
            isLoading: false
        });

    };
    render() {
        return (
            <PageLayout heading="Clients" icon={Users} className="!px-0" actions={this.renderPageActions()}>
                <AppCard className="mb-3">
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            header={this.renderHeader}
                            ref={(ref) => (this.dataTable = ref)}
                            value={this.state.clients}
                            onChange={this.loadClientsData}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            onRowClick={this.onClientDetailsActionButtonClick}
                            defaultSortField="requestDt"
                            defaultSortOrder={SortOrderType.Descending}
                        >
                            <Column {...({ alwaysVisible: true } as any)} filterMatchModeOptions={numberFilterMatchModeOptions} field="individualId" header="Individual Id" body={(rowData: IssuingClient) =>
                                this.renderRowLink(rowData, rowData.individualId)
                            }></Column>
                            <Column
                                header="Name"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: IssuingClient) =>
                                    <Link
                                        to={`/user-profile/${rowData.individualId}`}
                                        style={{ color: "#0d6efd", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {rowData.name}
                                    </Link>
                                }
                            />
                            <Column field="ClientID" header="Client ID" {...({ alwaysVisible: true } as any)} body={
                                (rowData: IssuingClient) => this.renderRowLink(rowData, rowData.icardClientId)
                            }></Column>

                            <Column field="cbsId" header="CbsId" body={
                                (rowData: IssuingClient) => this.renderRowLink(rowData, rowData.flexCubeCbsId)
                            }></Column>
                            <Column
                                field="clientStatus"
                                header="ICard Status"
                                body={(rowData: IssuingClient) =>
                                    this.renderRowLink(rowData, this.renderStatusBadge(rowData.clientStatus))
                                }
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                            ></Column>
                            <Column
                                field="requestDt"
                                header="Requested Date"
                                body={(rowData: IssuingClient) => (
                                    this.renderRowLink(rowData, <p>{moment.utc(rowData.requestDt).local().format("DD/MM/YYYY HH:mm:ss")}</p>)
                                )}
                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                dataType="date"
                                filterElement={dateFilterTemplate}
                            ></Column>
                            {/* {(isMerchantCreateNewAllowed() || isMerchantUpdateAllowed()) && ( */}

                            {/* )} */}
                        </ExtendedDataTable>
                        <Dialog
                            header="Enroll To ICard"
                            visible={this.state.showAddDialog}
                            style={{ width: "70vw" }}
                            contentStyle={{ maxHeight: "60vh", overflowY: "auto" }}
                            onHide={() => this.setState({ showAddDialog: false })}
                        >
                            <ExtendedDataTable
                                value={this.state.individuals}
                                loading={this.state.isLoading}
                                totalRecords={this.state.totalRecords}
                                onChange={this.loadUsersData}
                                defaultSortField="createdTimeStamp"
                                defaultSortOrder={SortOrderType.Descending}

                            >
                                <Column {...({ alwaysVisible: true } as any)} field="id" header="Id" filterMatchModeOptions={stringFilterMatchModeOptions}/>
                                <Column
                                    field="firstName"
                                    header="First Name"
                                    {...({ alwaysVisible: true } as any)}
                                    filterMatchModeOptions={stringFilterMatchModeOptions}
                                    body={(rowData: User) => (
                                        <span
                                            style={{
                                                cursor: "pointer",
                                                color: this.state.selectedUser?.id === rowData.id ? "green" : "#0d6efd"
                                            }}
                                            onClick={() => this.onUserSelect(rowData)}
                                        >
                                            {rowData.firstName}
                                        </span>
                                    )}
                                />
                                <Column field="lastName" {...({ alwaysVisible: true } as any)} header="Last Name" filterMatchModeOptions={stringFilterMatchModeOptions}/>
                                <Column field="email" header="Email" filterMatchModeOptions={stringFilterMatchModeOptions}/>
                                <Column
                                    body={this.actionBodyTemplateUser}
                                    headerStyle={{ width: "8em", textAlign: "center" }}
                                    bodyStyle={{ textAlign: "center", overflow: "visible" }}
                                />
                            </ExtendedDataTable>


                        </Dialog>

                    </AppCard.Body>
                </AppCard>
            </PageLayout>

        );
    }
}
